import { Genders } from '@presto-assistant/api_types';
import { useCallback, useMemo } from 'react';
import Chip from '@mui/material/Chip';
import AccountCancelIcon from 'mdi-material-ui/AccountCancel';
import ArrowTopRightThickIcon from 'mdi-material-ui/ArrowTopRightThick';

import { createDataGridActionsColumn } from 'components/shared/TableDataGrid/helpers';
import { dateTimeColumn } from 'utils';
import { fetchUserToken } from 'utils/api';
import {
  getMemberStatusValue,
  memberStatusOptions,
  renderMemberStatusCell,
} from 'components/shared/memberStatusColumnHelpers';
import { useGetGenderOptions } from 'hooks/useGetGenderOptions';
import type { DataGridColDef } from 'types/dataGrid';
import type { MoreActionsItem } from 'components/shared/TableDataGrid/ToolbarMoreActionsIconMenu';

import type { OrganizationDirectorRow } from './types';

const chipStyles = {
  borderWidth: 1.5,
  marginLeft: 0.5,
};

export const useColumns = ({
  fraudUserIds,
  onFraudActionClick,
}: {
  fraudUserIds: string[],
  onFraudActionClick: MoreActionsItem<OrganizationDirectorRow>['action'];
}) => {
  const genderOptions = useGetGenderOptions();

  const handleSwitchToUser = useCallback(async (row: OrganizationDirectorRow) => {
    const tokenForBecomeUser = await fetchUserToken(row?.id ?? '');

    window.open(
      `${process.env.REACT_APP_CLIENT_URL}?token=${tokenForBecomeUser}`,
      '_blank',
    );
  }, []);

  const columns = useMemo<DataGridColDef<OrganizationDirectorRow>[]>(() => {
    const baseColumns: DataGridColDef<OrganizationDirectorRow>[] = [
      {
        field: 'firstName',
        headerName: 'First Name',
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
      },
      {
        field: 'email',
        headerName: 'Email',
      },
      {
        field: 'title',
        headerName: 'Title',
      },
      {
        field: 'authUserId',
        headerName: 'Member Status',
        renderCell: (params) => {
          const doesUserHaveFraudStatus = fraudUserIds.includes(params.row.authUserId ?? '');

          const memberStatusCell = renderMemberStatusCell(params.value as string);

          return (
            <span>
              {memberStatusCell}
              {doesUserHaveFraudStatus && (
                <Chip
                  color="error"
                  label="Fraud"
                  size="small"
                  sx={chipStyles}
                  variant="outlined"
                />
              )}
            </span>
          );
        },
        type: 'singleSelect',
        valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
        valueGetter: (params) => getMemberStatusValue({
          authUserId: params.row.authUserId,
        }),
        valueOptions: memberStatusOptions,
      },
      {
        field: 'gender',
        headerName: 'Gender',
        renderCell: (params) => (params.row.gender?.id === Genders.Other.toString()
          ? `Other ${params.row.otherGenderLabel
            ? ` - ${params.row.otherGenderLabel}`
            : ''}`
          : undefined),
        type: 'singleSelect',
        valueGetter: (params) => params.row.gender?.id,
        valueOptions: genderOptions.options,
      },
      {
        field: 'phoneNumber',
        headerName: 'Phone Number',
      },
      dateTimeColumn({
        field: 'loggedInAt',
        headerName: 'Last Logged In',
      }),
    ];

    const actionsColumn = createDataGridActionsColumn<OrganizationDirectorRow>([
      {
        action: handleSwitchToUser,
        icon: <ArrowTopRightThickIcon />,
        text: 'Switch to User',
      },
      {
        action: onFraudActionClick,
        icon: <AccountCancelIcon />,
        isDisabled: (params) => !params.authUserId,
        text: 'Update Fraud Status',
      },
    ]);

    return actionsColumn ? [...baseColumns, actionsColumn] : baseColumns;
  }, [genderOptions.options, handleSwitchToUser]);

  return columns;
};
